import { makeStyles, useTheme } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  BLOG_PAGE,
  EDITOR_HOST,
  EDITOR_PAGE,
  GALLERY_PAGE,
  INDUSTRIES,
  PRICING,
  PRODUCTS_PAGE,
  SCANNER_PAGE,
  SIGN_IN,
  SUPPORT_PAGE,
  USER_GUIDE_PAGE,
  WHITE_LABEL_PAGE,
  PERFORMLIVE
} from '../../links';
import MenuIcon from '../icons/menu';

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  mobBtn: {
    '&:active': {
      backgroundColor: 'transparent'
    }
  },
  hide: {
    display: 'none'
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar
  },
  drawerList: {
    border: 'none',
    '& li': {
      cursor: 'pointer'
    },
    '& a': {
      color: 'inherit',
      textDecoration: 'none',
      '&.perform': {
        color: "#FF416E",
        letterSpacing: 1,
        lineHeight: 1.375,
        textDecoration: 'none',
        '&:hover': {
          color: "#02BBD3"
        }
      }
    }
  },
  nestedDrawerListItem: {
    paddingLeft: theme.spacing(8)
  },
  grow: {
    flexGrow: 1
  },
  collapsedItem: {
    flexWrap: 'wrap'
  },
  subList: {
    flex: '0 0 100%',
    '& ul': {
      border: 'none'
    }
  }
}));

const MobileMenu = ({ user, isColor, isAuthenticated }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpenDrawer, setOpenDrawer] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const handleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="end"
        onClick={handleDrawerOpen}
        className={clsx(isOpenDrawer && classes.hide, classes.mobBtn)}
      >
        <MenuIcon fill={isColor ? '#02BBD3' : '#fff'} />
      </IconButton>
      <Drawer
        className={classes.drawer}
        variant="temporary"
        anchor="right"
        open={isOpenDrawer}
        onClose={handleDrawerClose}
        classes={{
          paper: classes.drawerPaper
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </div>
        <Divider />
        <List className={classes.drawerList} disablePadding>
          <ListItem className={classes.collapsedItem} onClick={handleCollapsed}>
            <ListItemText>{t('headerMenu.Solutions')}</ListItemText>
            {isCollapsed ? <ExpandLess /> : <ExpandMore />}
            <Collapse
              className={classes.subList}
              in={isCollapsed}
              timeout="auto"
              unmountOnExit
            >
              <List disablePadding>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={`/${INDUSTRIES}`}>
                      {t('headerMenu.Industries')}
                    </Link>
                  </ListItemText>
                </ListItem>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={PRODUCTS_PAGE}>{t('headerMenu.Products')}</Link>
                  </ListItemText>
                </ListItem>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={GALLERY_PAGE}>{t('headerMenu.Gallery')}</Link>
                  </ListItemText>
                </ListItem>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={EDITOR_PAGE}>{t('headerMenu.Editor')}</Link>
                  </ListItemText>
                </ListItem>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={SCANNER_PAGE}>{t('headerMenu.Scanner')}</Link>
                  </ListItemText>
                </ListItem>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={WHITE_LABEL_PAGE}>{t('headerMenu.WhiteLabel')}</Link>
                  </ListItemText>
                </ListItem>
              </List>
            </Collapse>
          </ListItem>
          
          <ListItem onClick={handleDrawerClose}>
            <ListItemText>
              <a href={PRICING}>{t('headerMenu.Pricing')}</a>
            </ListItemText>
          </ListItem>
          <ListItem onClick={handleDrawerClose}>
            <ListItemText>
              <Link to={BLOG_PAGE}>{t('headerMenu.Blog')}</Link>
            </ListItemText>
          </ListItem>
          <ListItem className={classes.collapsedItem} onClick={handleCollapsed}>
            <ListItemText>{t('headerMenu.Support')}</ListItemText>
            {isCollapsed ? <ExpandLess /> : <ExpandMore />}
            <Collapse
              className={classes.subList}
              in={isCollapsed}
              timeout="auto"
              unmountOnExit
            >
              <List disablePadding>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={SUPPORT_PAGE}>{t('headerMenu.Support')}</Link>
                  </ListItemText>
                </ListItem>
                <ListItem onClick={handleDrawerClose}>
                  <ListItemText>
                    <Link to={USER_GUIDE_PAGE}>
                      {t('headerMenu.UserGuide')}
                    </Link>
                  </ListItemText>
                </ListItem>
              </List>
            </Collapse>
          </ListItem>
          <ListItem onClick={handleDrawerClose}>
            <ListItemText>
              <a href={PERFORMLIVE} className="perform" target="_blank">
                Performlive
              </a>
            </ListItemText>
          </ListItem>
          <ListItem onClick={handleDrawerClose}>
            <ListItemText>
              <a href={isAuthenticated ? EDITOR_HOST : SIGN_IN}>
                {t('headerMenu.Create_AR')}
              </a>
            </ListItemText>
          </ListItem>
          <ListItem onClick={handleDrawerClose}>
            <ListItemText>
              {!isAuthenticated && (
                <a href={SIGN_IN}>{t('headerMenu.Sign_in')}</a>
              )}
            </ListItemText>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

MobileMenu.propTypes = {
  // eslint-disable-next-line react/require-default-props
  user: PropTypes.shape({
    id: PropTypes.number
  }),
  isColor: PropTypes.bool,
  isAuthenticated: PropTypes.bool
};

MobileMenu.defaultProps = {
  isColor: false
};

export default MobileMenu;
